<template>
    <div class="tw:px-4 tw:py-4">
        <div v-if="loading" class="tw:flex tw:justify-center tw:py-8">
            <div class="tw:animate-spin tw:rounded-full tw:h-8 tw:w-8 tw:border-b-2 tw:border-blue-600"></div>
        </div>

        <div v-else-if="error" class="tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:p-4">
            <p class="tw:text-red-700 tw:text-sm">{{ error }}</p>
            <button class="tw:text-blue-600 tw:text-sm tw:underline tw:mt-2" @click="$emit('retry')">Reintentar</button>
        </div>

        <template v-else-if="hasFamily">
            <div class="tw:mb-4">
                <div class="tw:flex tw:items-center tw:gap-2 tw:mb-2">
                    <h4 class="tw:text-sm tw:font-semibold tw:text-gray-900 tw:m-0">{{ family?.nombre || 'Sin nombre' }}</h4>
                    <button
                        class="tw:inline-flex tw:items-center tw:justify-center tw:w-8 tw:h-8 tw:rounded-lg tw:text-blue-600 hover:tw:bg-blue-50 tw:transition-colors"
                        title="Editar nombre"
                        @click="openEditNombreModal"
                    >
                        <i class="material-icons tw:text-lg">edit</i>
                    </button>
                    <span
                        class="tw:inline-flex tw:items-center tw:px-2.5 tw:py-0.5 tw:rounded-full tw:text-xs tw:font-medium"
                        :class="estadoBadgeClass(family?.estado)"
                    >
                        {{ estadoLabel(family?.estado) }}
                    </span>
                </div>
                <div
                    v-if="family && (family.estado === 'aprobado' || family.estado === 'rechazado')"
                    class="tw:rounded-lg tw:border tw:p-2 tw:text-xs"
                    :class="{
                        'tw:border-green-200 tw:bg-green-50 tw:text-green-800': family.estado === 'aprobado',
                        'tw:border-red-200 tw:bg-red-50 tw:text-red-800': family.estado === 'rechazado',
                    }"
                >
                    <i
                        class="material-icons tw:text-xs tw:align-middle tw:mr-1"
                    >{{ family.estado === 'aprobado' ? 'check_circle' : 'cancel' }}</i>
                    <template v-if="family.estado === 'rechazado' && family.motivo_rechazo">
                        <strong>Motivo del rechazo:</strong> {{ family.motivo_rechazo }}
                        <br/>
                    </template>
                    <span class="tw:opacity-75">
                        Revisado por {{ family.revisado_por?.name || '—' }} el {{ family.revisado_en || '—' }}
                    </span>
                </div>
            </div>

            <div class="tw:flex tw:justify-end tw:mb-4">
                <Button icon="fas fa-plus" @click="openAddModal">Añadir miembro</Button>
            </div>

            <div class="tw:bg-white tw:rounded-xl">
                <BaseTable
                    :columns="columns"
                    :data="members"
                    :loading="loading"
                    :skeleton="loading"
                    :skeleton-rows="5"
                    empty-text="No hay miembros en esta familia"
                    :default-col-width="defaultColWidth"
                >
                    <template #skeleton-header-name>
                        <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-14"></div>
                    </template>
                    <template #skeleton-header-n_abonado>
                        <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-20"></div>
                    </template>
                    <template #skeleton-header-dni>
                        <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-8"></div>
                    </template>
                    <template #skeleton-header-tipo_abono>
                        <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-16"></div>
                    </template>
                    <template #skeleton-name>
                        <div class="tw:flex tw:items-center tw:gap-2">
                            <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-32"></div>
                            <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-14"></div>
                        </div>
                    </template>
                    <template #skeleton-n_abonado>
                        <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-14"></div>
                    </template>
                    <template #skeleton-dni>
                        <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-24"></div>
                    </template>
                    <template #skeleton-tipo_abono>
                        <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-20"></div>
                    </template>
                    <template #skeleton-acciones>
                        <div class="tw:h-8 tw:w-20 tw:bg-gray-200 tw:rounded"></div>
                    </template>

                    <template #cell-name="{ row }">
                        <div class="tw:flex tw:items-center tw:gap-2">
                            <span class="tw:font-medium tw:text-gray-900">{{ row.name }} {{ row.surnames }}</span>
                            <span v-if="isTitular(row)" class="tw:inline-flex tw:items-center tw:px-1.5 tw:py-0.5 tw:rounded tw:text-xs tw:font-medium tw:bg-blue-100 tw:text-blue-700">Titular</span>
                        </div>
                    </template>
                    <template #cell-n_abonado="{ row }">
                        <span class="tw:text-sm tw:text-gray-600">{{ row.n_abonado }}</span>
                    </template>
                    <template #cell-dni="{ row }">
                        <span class="tw:text-sm tw:text-gray-600">{{ row.dni }}</span>
                    </template>
                    <template #cell-tipo_abono="{ row }">
                        <span class="tw:text-sm tw:text-gray-600">{{ row.tipo_abono || '-' }}</span>
                    </template>
                    <template #cell-acciones="{ row }">
                        <OptionsDropdownComponent :options="getRowOptions(row)" />
                    </template>
                </BaseTable>
            </div>

            <AddMiembroModal @add-member="handleAdd" @close="closeAddModal" />

            <RenameFamiliaModal
                v-if="editNombreOpen"
                :name="modalName"
                :nombre="editNombreValue"
                :original="family?.nombre"
                :loading="editNombreLoading"
                :error="editNombreError"
                @update:nombre="editNombreValue = $event"
                @submit="handleEditNombre"
                @close="closeEditNombreModal"
            />

            <BorrarModalComponent
                v-if="memberToRemove"
                :name="'borrar-miembro-' + memberToRemove.id"
                title="Eliminar miembro"
                @confirm="confirmRemove"
                @cancel="cancelRemove"
                @close="cancelRemove"
            >
                ¿Está seguro de que quiere eliminar a {{ memberToRemove.name }} {{ memberToRemove.surnames }} de la familia?
            </BorrarModalComponent>
        </template>

        <template v-else>
            <div>
                <h4 class="tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-3">Crear familia</h4>
                <p class="tw:text-sm tw:text-gray-500 tw:mb-4">Este abonado aún no tiene una familia asociada.</p>
                <div class="tw:flex tw:items-end tw:gap-3">
                    <div class="tw:flex-1">
                        <BaseInputText label="Nombre de la familia" name="nombre_familia" :value="createName" placeholder="Opcional" @input="createName = $event" />
                    </div>
                    <Button :disabled="createLoading" @click="handleCreate">
                        {{ createLoading ? 'Creando...' : 'Crear familia' }}
                    </Button>
                </div>
                <span v-if="apiCreateError" class="tw:text-xs tw:text-red-500 tw:mt-2 tw:inline-block tw:leading-tight" v-html="apiCreateError.replace(/\n/g, '<br>')"></span>
            </div>
        </template>
    </div>
</template>

<script setup>
    import { ref, getCurrentInstance, nextTick, watch, reactive } from 'vue'
    import Button from '@/modules/admin/ui/Button.vue'
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue'
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue'
    import OptionsDropdownComponent from '@/modules/admin/ui/OptionsDropdownComponent.vue'
    import BorrarModalComponent from '@/modules/admin/ui/BorrarModalComponent.vue'
    import AddMiembroModal from '@/modules/admin/abonados/familias/modals/AddMiembroModal.vue'
    import RenameFamiliaModal from '@/modules/admin/abonados/familias/modals/RenameFamiliaModal.vue'
    import { estadoBadgeClass, estadoLabel } from '@/modules/admin/abonados/familias/utils.js'

    const props = defineProps({
        family: { type: Object, default: null },
        members: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        error: { type: String, default: null },
        hasFamily: { type: Boolean, default: false },
        abonado: { type: Object, default: null },
        createLoading: { type: Boolean, default: false },
        apiCreateError: { type: String, default: null },
        defaultColWidth: {
            type: String,
            default: '120px'
        }
    })

    const emit = defineEmits(['add-member', 'remove-member', 'create-family', 'retry', 'update-nombre'])
    const instance = getCurrentInstance()
    const modalName = `editar-nombre-miembros-tab-${instance.uid}`

    const memberToRemove = ref(null)
    const createName = ref('')

    // Edit nombre state
    const editNombreOpen = ref(false)
    const editNombreValue = ref('')
    const editNombreLoading = ref(false)
    const editNombreError = ref(null)

    function openEditNombreModal() {
        editNombreValue.value = props.family?.nombre || ''
        editNombreError.value = null
        editNombreOpen.value = true
        nextTick(() => instance.proxy.$modal.show(modalName))
    }

    function closeEditNombreModal() {
        instance.proxy.$modal.hide(modalName)
        editNombreOpen.value = false
        editNombreValue.value = ''
        editNombreLoading.value = false
        editNombreError.value = null
    }

    function handleEditNombre(nombre) {
        if (!nombre || nombre === (props.family?.nombre || '').trim()) return
        editNombreLoading.value = true
        editNombreError.value = null
        emit('update-nombre', {
            nombre,
            onSuccess: () => closeEditNombreModal(),
            onError: msg => { editNombreError.value = msg; editNombreLoading.value = false },
        })
    }

    const columns = [
        { key: 'name', label: 'Nombre', class: 'tw:w-64' },
        { key: 'n_abonado', label: 'Nº Abonado', centered:true },
        { key: 'dni', label: 'DNI' },
        { key: 'tipo_abono', label: 'Tipo Abono' },
        { key: 'acciones', label: '' },
    ]

    function openAddModal() {
        instance.proxy.$modal.show('add-miembro-familia')
    }

    function closeAddModal() {
        // Modal self-closes via ModalBase
    }

    function isTitular(row) { return props.family?.titular?.id === row.id }

    function getRowOptions(row) {
        return [{ type: 'action', action: () => openRemoveModal(row), label: 'Eliminar', icon: 'trash' }]
    }

    function openRemoveModal(row) {
        memberToRemove.value = row
        nextTick(() => instance.proxy.$modal.show('borrar-miembro-' + row.id))
    }

    function handleAdd(payload) {
        emit('add-member', payload)
    }

    function confirmRemove() {
        const id = memberToRemove.value?.id
        instance.proxy.$modal.hide('borrar-miembro-' + id)
        memberToRemove.value = null
        if (id) emit('remove-member', id)
    }

    function cancelRemove() {
        if (memberToRemove.value) instance.proxy.$modal.hide('borrar-miembro-' + memberToRemove.value.id)
        memberToRemove.value = null
    }

    function handleCreate() {
        emit('create-family', { nombre: createName.value.trim() })
    }
</script>
